import React from 'react'

import { Box, Text } from '@chakra-ui/react'
import { ItListItem } from 'types/graphql'

import { useWatch } from '@redwoodjs/forms'

import PromoCode from 'src/components/features/EditItList/PromoCode/PromoCode'
import ErrorMessage from 'src/components/widgets/ErrorMessage/ErrorMessage'
import Input from 'src/components/widgets/Input/Input'
import RevealInput from 'src/components/widgets/RevealInput/RevealInput'
import VoiceTipRecorder from 'src/components/widgets/VoiceTipRecorder/VoiceTipRecorder'
import { MAX_LG_INPUT, MAX_LG_INPUT_NO_COUNTER } from 'src/lib/constants'
import useGetError from 'src/lib/hooks/form/useGetError'
import { matchUrl } from 'src/lib/utils'

import { TypeBuilderProps } from '../..'
import EditItemImage from '../../EditItemImage/EditItemImage'
import Scraper from '../../Scraper/Scraper'

const Body: React.FC<Omit<TypeBuilderProps, 'onUpdate' | 'index'>> = ({
  voiceTipRecorderProps,
  itemId,
}) => {
  const item = useWatch<ItListItem>() as ItListItem
  const amazonUrlErrors = useGetError<ItListItem>('product.amazonUrl')
  const missingURLsError = () =>
    amazonUrlErrors?.type === 'custom' ? amazonUrlErrors : null

  return (
    <Box mt={4} w="full">
      {missingURLsError() && (
        <ErrorMessage message={missingURLsError().message.toString()} mb={2} />
      )}
      <Box mb={4}>
        <RevealInput
          type="url"
          name="url"
          placeholder="Product URL"
          mb={4}
          title="Add Direct URL"
          maxLength={2000}
          testid="productBuilder-productURL"
        />
      </Box>
      <RevealInput
        type="url"
        name="product.amazonUrl"
        placeholder="Amazon URL"
        title="Add Amazon URL"
        maxLength={2000}
        testid="productBuilder-amazonURL"
      />

      <Text as="h5" className="thick" mt={12} mb={4}>
        About Product
      </Text>
      <Input
        w="full"
        name="title"
        placeholder="Title"
        clearable
        data-testid="productBuilder-productTitle"
        withCounter={true}
        maxLength={MAX_LG_INPUT}
        hideCounterMax={MAX_LG_INPUT_NO_COUNTER}
      />
      <EditItemImage
        name="product.coverUrl"
        borderRadius={12}
        assetType="itListItemCover"
        assetId={itemId}
        width={350}
        height={350}
      />
      <VoiceTipRecorder
        name="hasVoiceTip"
        mt={4}
        title="Voice Tip"
        subtitle="Optional"
        showProfileImage={true}
        {...voiceTipRecorderProps}
      />
      <Text color="stone" m={4} mt={2} className="body-extra-small">
        Helpful context boosts conversions.
      </Text>
      <PromoCode item={item} />
    </Box>
  )
}

const Builder: React.FC<TypeBuilderProps> = ({
  voiceTipRecorderProps,
  itemId,
}) => {
  return (
    <Scraper
      onBeforeUpdate={(item) => {
        if (isAmazonUrl(item.url)) {
          item.product.amazonUrl = item.url
          item.url = null
        }

        return item
      }}
    >
      <Body voiceTipRecorderProps={voiceTipRecorderProps} itemId={itemId} />
    </Scraper>
  )
}

const isAmazonUrl = (url: string) =>
  ['amazon.com', 'a.co', 'amzn.to'].some((site) => matchUrl(site, url))

export default Builder
